import { useEffect, useState, type ComponentType, type ReactElement } from 'react'

import { TUseInterIcon } from '../types/InterSvgIcon.types'

type IconComponent = ComponentType<{
  onClick?: () => void
  width?: number | string
  height?: number | string
  color?: string
}>

const iconCache = new Map<string, Promise<IconComponent>>()

const loadIcon = (iconRef: string) => {
  const cachedIcon = iconCache.get(iconRef)

  if (cachedIcon) return cachedIcon

  const loader = import(`@interco/icons/${iconRef}/index.js`)
    .then((module) => module.default as IconComponent)
    .catch((error) => {
      iconCache.delete(iconRef)
      throw error
    })

  iconCache.set(iconRef, loader)

  return loader
}

export const prefetchInterSvgIcons = async (iconRefs: string[]) => {
  if (!iconRefs.length) return

  await Promise.all(iconRefs.map((iconRef) => loadIcon(iconRef)))
}

export function useInterSvgIcon({
  iconRef,
  skeletonShape,
  height,
  width,
  color,
  onClick,
}: TUseInterIcon) {
  const [importedIcon, setImportedIcon] = useState<ReactElement | null>(null)

  useEffect(() => {
    let isCancelled = false

    loadIcon(iconRef)
      .then((ImportedComponent) => {
        if (isCancelled) return

        setImportedIcon(
          <ImportedComponent onClick={onClick} width={width} height={height} color={color} />,
        )
      })
      .catch((error: unknown) => {
        if (process.env.NODE_ENV !== 'production') {
          global.console.error('Failed to load Inter icon', { iconRef, error })
        }
      })

    return () => {
      isCancelled = true
    }
  }, [iconRef, width, height, color, onClick])

  return {
    borderRadius: skeletonShape === 'SQUARE' ? '4px' : '50%',
    importedIcon,
  }
}
